'use client';

import * as React from 'react';
import { CircleCheck, CircleX, TriangleAlert, type LucideIcon } from 'lucide-react';
import { domId } from '@/lib/dom-id';
import {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from './toast';

export interface Toast {
  id: string;
  title?: string;
  description?: string;
  action?: ToastActionElement;
  variant?: "default" | "success" | "warning" | "error" | "destructive";
  duration?: number;
}

type ToastActionElement = React.ReactElement<typeof ToastAction>;

const VARIANT_ICONS: Partial<Record<NonNullable<Toast['variant']>, { icon: LucideIcon; className: string }>> = {
  success: { icon: CircleCheck, className: 'text-green-600' },
  warning: { icon: TriangleAlert, className: 'text-yellow-600' },
  error: { icon: CircleX, className: 'text-red-600' },
  destructive: { icon: CircleX, className: 'text-white' },
};

export function useToast() {
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  const addToast = React.useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts((prev) => [...prev, { ...toast, id }]);
    return id;
  }, []);

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return { toasts, addToast, removeToast };
}

export interface ToasterProps {
  toasts: Toast[];
  onToastClose: (id: string) => void;
}

export function Toaster({ toasts, onToastClose }: ToasterProps) {
  return (
    <ToastProvider>
      {toasts.map(
        ({
          id,
          title,
          description,
          action,
          variant = "default",
          duration = 3000,
        }) => {
        const variantIcon = VARIANT_ICONS[variant];
        const Icon = variantIcon?.icon;
        return (
        <Toast
          key={id}
          data-testid={domId('div', `toast-${variant}`)}
          variant={variant}
          duration={duration}
          onOpenChange={(open) => !open && onToastClose(id)}
        >
          {Icon && (
            <Icon className={`h-5 w-5 shrink-0 ${variantIcon.className}`} aria-hidden="true" />
          )}
          <div className="grid min-w-0 flex-1 gap-1">
            {title && <ToastTitle>{title}</ToastTitle>}
            {description && <ToastDescription>{description}</ToastDescription>}
          </div>
          {action}
          <ToastClose data-testid={domId('btn', 'toast-close')} />
        </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}
